import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for URL validation
 */
export interface UrlValidatorOptions extends BaseValidatorOptions {
  requireProtocol?: boolean;
}

/**
 * Validate a URL value
 * Only accepts http:// and https:// protocols
 * @param value - The URL string to validate
 * @param options - Validation options
 * @returns Validation result with the URL value and optional error (never null)
 *
 * @example
 * ```typescript
 * validateUrl("https://example.com");     // { value: "https://example.com" }
 * validateUrl("http://example.com");      // { value: "http://example.com" }
 * validateUrl("ftp://example.com");       // { value: "ftp://example.com", error: { ... } }
 * validateUrl("not-a-url");               // { value: "not-a-url", error: { ... } }
 * ```
 */
export function validateUrl(
  value: string,
  { field = "value", requireProtocol = true }: UrlValidatorOptions = {}
): ValidationResult<string> {
  try {
    const url = new URL(requireProtocol ? value : `http://${value}`);
    // Only accept http/https protocols
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return {
        value,
        error: {
          field,
          message: `"${field}" does not match required url pattern: ${value}`,
          severity: "warning",
        },
      };
    }
    return { value };
  } catch {
    return {
      value,
      error: {
        field,
        message: `"${field}" does not match required url pattern: ${value}`,
        severity: "warning",
      },
    };
  }
}
